import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ordersAPI } from '../services/api';
import { Edit2, Package } from 'lucide-react';
import { createNavigationHelpers } from '../utils/navigationHelpers';
import { usePermissions } from '../hooks/usePermissions';

const Orders = () => {

  const [statusFilter, setStatusFilter] = useState('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { goToEdit } = createNavigationHelpers('orders', navigate);
  const { orders: orderPerms } = usePermissions();

  // Fetch orders
  const { data: ordersData, isLoading, error } = useQuery(
    ['orders', statusFilter, orderTypeFilter, dateFilter],
    () => {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (orderTypeFilter !== 'all') params.orderType = orderTypeFilter;
      if (dateFilter) params.date = dateFilter;
      return ordersAPI.getAll(params);
    },
    {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onSuccess: (data) => {
        console.log('Orders query success:', data);
      },
      onError: (error) => {
        console.error('Orders fetch error:', error);
        toast.error('Failed to load orders');
      }
    }
  );

  // Update order status mutation
  const updateStatusMutation = useMutation(
    ({ uuid, status }) => ordersAPI.updateStatus(uuid, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['orders']);
        toast.success('Order status updated successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'An error occurred');
      },
    }
  );

  const handleStatusUpdate = (orderUuid, newStatus) => {
    updateStatusMutation.mutate({ uuid: orderUuid, status: newStatus });
  };

  const handleViewOrder = (order) => {
    goToEdit(order.uuid);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Debug logging
  console.log('=== ORDERS DEBUG ===');
  console.log('Raw ordersData:', ordersData);
  console.log('ordersData?.data:', ordersData?.data);
  console.log('ordersData?.data?.data:', ordersData?.data?.data);
  
  // Extract orders from the correct path in the API response
  let orders = [];
  
  // First check if we have the nested structure (data.data.data)
  if (Array.isArray(ordersData?.data?.data)) {
    orders = ordersData.data.data;
    console.log('Using nested data structure for orders');
  } 
  // Then check if we have a simpler structure (data.data)
  else if (Array.isArray(ordersData?.data)) {
    orders = ordersData.data;
    console.log('Using simple data structure for orders');
  }
  
  console.log('Extracted orders:', orders.length);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Failed to load orders</div>
          <div className="text-gray-500 dark:text-gray-400 text-sm">Please try refreshing the page</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card-gradient p-4 sm:p-6 text-center rounded-xl animate-fade-in-up">
        <div className="text-left">
          <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2">Orders</h1>
          <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">
            Manage customer orders and track deliveries
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card-modern animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white shadow-glow">
            <Package className="h-5 w-5" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Filters</h3>
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="space-y-2 flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-modern"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>
          <div className="space-y-2 flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order Type</label>
            <select
              value={orderTypeFilter}
              onChange={(e) => setOrderTypeFilter(e.target.value)}
              className="input-modern"
            >
              <option value="all">All Types</option>
              <option value="regular">Regular Orders</option>
              <option value="subscription">Subscription Orders</option>
            </select>
          </div>
          <div className="space-y-2 flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order Date</label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="input-modern"
            />
          </div>
        </div>
      </div>

      {/* Orders Table - Responsive */}
      <div className="card-modern animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
        {/* Desktop Table View */}
        <div className="hidden md:block">
          <div className="overflow-x-auto">
            <table className="table-modern">
              <thead>
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => (
                  <tr key={order.uuid} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      #{order.order_number || order.uuid}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <div>
                        <div className="font-medium">{order.customer_name}</div>
                        <div className="text-gray-500 dark:text-gray-400">{order.customer_email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        order.order_type === 'subscription' 
                          ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' 
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                      }`}>
                        {order.order_type === 'subscription' ? '📦 Subscription' : '🛒 Regular'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {order.items_count} items
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(order.total_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        {/* Edit Button - Only show if user can update orders */}
                        {orderPerms.canUpdate && (
                          <button
                            onClick={() => goToEdit(order.uuid)}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 flex items-center gap-1 transition-colors"
                          >
                            <Edit2 className="h-3 w-3" />
                            Edit
                          </button>
                        )}
                        
                        {/* Status Update - Only show if user can update orders */}
                        {orderPerms.canUpdate && order.status !== 'delivered' && (
                          <select
                            value={order.status}
                            onChange={(e) => handleStatusUpdate(order.uuid, e.target.value)}
                            className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="delivered">Delivered</option>
                          </select>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {orders.map((order, index) => (
            <div 
              key={order.uuid} 
              className="table-card animate-fade-in-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
                <div className="table-card-header">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      #{order.order_number || order.uuid}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </div>
                
                <div className="space-y-1 mb-3">
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Customer:</span>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{order.customer_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{order.customer_email}</p>
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Items:</span>
                    <span className="text-sm text-gray-900 dark:text-white">{order.items_count} items</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Total:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(order.total_amount)}</span>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  {/* Edit Button - Only show if user can update orders */}
                  {orderPerms.canUpdate && (
                    <button
                      onClick={() => goToEdit(order.uuid)}
                      className="flex-1 bg-indigo-600 text-white text-sm py-2 px-3 rounded-md hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1"
                    >
                      <Edit2 className="h-3 w-3" />
                      Edit Order
                    </button>
                  )}
                  
                  {/* Status Update - Only show if user can update orders */}
                  {orderPerms.canUpdate && order.status !== 'delivered' && (
                    <select
                      value={order.status}
                      onChange={(e) => handleStatusUpdate(order.uuid, e.target.value)}
                      className="flex-1 text-sm border border-gray-300 rounded-md px-2 py-2 bg-white"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="delivered">Delivered</option>
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Empty State */}
        {orders.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No orders found</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {statusFilter !== 'all' ? `No orders with status "${statusFilter}"` : 'No orders have been placed yet'}
            </p>
          </div>
        )}
    </div>
  );
};

export default Orders; 